import { Component, ElementRef, OnInit, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { Select2Data } from 'ng-select2-component';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import { Offcanvas } from 'bootstrap';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

@Component({
  selector: 'app-enquiry-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BreadcrumbComponent,
    NgSelectModule,
    CKEditorModule
  ],
  templateUrl: './enquiry-edit.component.html',
  styleUrls: ['./enquiry-edit.component.css']
})
export class EnquiryEditComponent implements OnInit, AfterViewInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Enquiries',
    currentURL: 'Edit Enquiry',
    breadcrumb_url: '/admin/enquiries'
  };

  enquiryForm!: FormGroup;
  enquiryId: string = '';
  enquiryDetails: any = null;

  // Status options
  statusOptions = [
    { value: 'HOT', label: 'Hot' },
    { value: 'WARM', label: 'Warm' },
    { value: 'COLD', label: 'CoLD' },
    { value: 'CONVERTED', label: 'Converted' },
    { value: 'REJECT', label: 'Reject' },
  ];

  // Property dropdown with lazy loading
  propertiesList: any[] = [];
  propertiesItems: Select2Data = [];
  searchProperty$ = new Subject<string>();
  private propertySearchSub?: Subscription;
  private propertyPage = 1;
  private propertyPageSize = 20;
  private hasMoreProperties = true;

  // Comments
  comments: any[] = [];
  commentForm!: FormGroup;
  drawerOpen: boolean = false;
  Editor: any = ClassicEditor;

  @ViewChild('addCommentDrawer') offcanvas!: ElementRef;
  private offcanvasInstance: Offcanvas | null = null;

  private subscriptions: Subscription = new Subscription();

  /** Filter state handed over by the list page, replayed on Back/Save. */
  private listFilters: Params = {};

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private httpService: HttpServiceService,
    public loadingService: LoadingService,
    private authService: LocalStorageService,
    private alertService: NgxToastrService
  ) {
    this.initForm();
    this.commentForm = this.fb.group({
      comment: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.setupPropertySearch();
    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        this.listFilters = params;
      })
    );
    const paramSub = this.route.paramMap.subscribe(params => {
      this.enquiryId = params.get('id') || '';
      if (this.enquiryId) {
        this.getEnquiryDetails();
        if (this.checkViewCommentPermission) {
          this.getComments();
        }
      }
    });
    this.subscriptions.add(paramSub);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const offcanvasElement = this.offcanvas?.nativeElement;
      if (offcanvasElement) {
        this.offcanvasInstance = Offcanvas.getOrCreateInstance(offcanvasElement, {
          backdrop: 'static',
          keyboard: false,
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.propertySearchSub?.unsubscribe();
  }

  initForm(): void {
    this.enquiryForm = this.fb.group({
      property_id: ['', Validators.required],
      property_type: [''],
      name: ['', Validators.required],
      phone: ['', Validators.required],
      email: ['', [Validators.email]],
      locality: [''],
      city: [''],
      state: [''],
      amount: [''],
      bath_room: [''],
      looking_for: [''],
      status: [''],
      follow_up_date: ['']
    });
  }

  setupPropertySearch(): void {
    this.propertySearchSub = this.searchProperty$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.propertyPage = 1;
          this.hasMoreProperties = true;
        }),
        switchMap((term) => {
          const query = term ? `&name=${term}` : '';
          return this.httpService.getAllProperties(
            this.propertyPage,
            this.propertyPageSize,
            query
          );
        })
      )
      .subscribe((response) => {
        this.propertiesList = response.data?.properties ?? [];
        this.hasMoreProperties =
          this.propertiesList.length < (response.data?.total ?? 0);
        this.propertiesItems = this.propertiesList.map((p) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name },
        }));
      });
    // Initial load
    this.searchProperty$.next('');
  }

  onPropertyScrollEnd(): void {
    if (!this.hasMoreProperties) return;
    this.propertyPage++;
    this.httpService
      .getAllProperties(this.propertyPage, this.propertyPageSize)
      .subscribe((resp) => {
        const more = resp.data?.properties ?? [];
        this.hasMoreProperties =
          this.propertiesList.length + more.length < (resp.data?.total ?? 0);
        this.propertiesList = [...this.propertiesList, ...more];
        this.propertiesItems = this.propertiesList.map((p) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name },
        }));
      });
  }

  getEnquiryDetails(): void {
    this.loadingService.show();
    const sub = this.httpService.getEnquiryDetailsById(this.enquiryId).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.enquiryDetails = response.data;

          // Check if platform is Direct - if not, redirect back
          if (this.enquiryDetails.platform !== 'Direct') {
            this.alertService.error('Only Direct enquiries can be edited', 'toast-top-right');
            this.backToEnquiries();
            return;
          }

          // Populate form with existing values
          this.enquiryForm.patchValue({
            property_id: this.enquiryDetails.property?.id || '',
            property_type: this.enquiryDetails.property_type || '',
            name: this.enquiryDetails.name || '',
            phone: this.enquiryDetails.phone || '',
            email: this.enquiryDetails.email || '',
            locality: this.enquiryDetails.locality || '',
            city: this.enquiryDetails.city || '',
            state: this.enquiryDetails.state || '',
            amount: this.enquiryDetails.amount || '',
            bath_room: this.enquiryDetails.bath_room || '',
            looking_for: this.enquiryDetails.looking_for || '',
            status: this.enquiryDetails.status || '',
            // <input type="date"> only accepts yyyy-MM-dd, so trim any
            // time part off the API value or the field stays empty.
            follow_up_date: this.enquiryDetails.follow_up_date
              ? String(this.enquiryDetails.follow_up_date).slice(0, 10)
              : ''
          });

          // If property exists, add it to the dropdown items
          if (this.enquiryDetails.property?.id) {
            const existingProperty = {
              value: this.enquiryDetails.property.id,
              label: this.enquiryDetails.property.name,
              data: { name: this.enquiryDetails.property.name }
            };
            // Check if it's not already in the list
            const exists = this.propertiesItems.some(p => (p as any).value === existingProperty.value);
            if (!exists) {
              this.propertiesItems = [existingProperty, ...this.propertiesItems];
            }
          }
        } else {
          this.alertService.error(response.message || 'Error fetching enquiry details', 'toast-top-right');
        }
      },
      error: () => {
        this.loadingService.hide();
      }
    });
    this.subscriptions.add(sub);
  }

  onSubmit(): void {
    if (this.enquiryForm.valid) {
      const formData = this.enquiryForm.value;

      // Add platform as 'Direct'
      const enquiryData = {
        ...formData,
        platform: 'Direct'
      };

      // Remove empty fields
      Object.keys(enquiryData).forEach(key => {
        if (enquiryData[key] === '' || enquiryData[key] === null) {
          delete enquiryData[key];
        }
      });

      this.loadingService.show();
      const sub = this.httpService.updateEnquiry(this.enquiryId, enquiryData).subscribe({
        next: (response: any) => {
          this.loadingService.hide();
          if (response.success) {
            this.alertService.success(response.message || 'Enquiry updated successfully', 'toast-top-right');
            this.backToEnquiries();
          } else {
            this.alertService.error(response.message || 'Error updating enquiry', 'toast-top-right');
          }
        },
        error: () => {
          this.loadingService.hide();
        }
      });
      this.subscriptions.add(sub);
    } else {
      this.markFormGroupTouched(this.enquiryForm);
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.enquiryForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  backToEnquiries(): void {
    this.router.navigate(['/admin/enquiries'], { queryParams: this.listFilters });
  }

  get checkAddCommentPermission() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_ADD_COMMENT);
  }

  get checkViewCommentPermission() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_VIEW_COMMENT);
  }

  getComments(): void {
    const sub = this.httpService.getEnquiryComments(this.enquiryId).subscribe({
      next: (response: any) => {
        this.comments = response.data;
      }
    });
    this.subscriptions.add(sub);
  }

  openCommentDrawer(): void {
    this.drawerOpen = true;
    if (this.offcanvasInstance) {
      this.offcanvasInstance.show();
    }
  }

  closeCommentDrawer(): void {
    this.drawerOpen = false;
    this.commentForm.reset();
    if (this.offcanvasInstance) {
      this.offcanvasInstance.hide();
    }
  }

  saveComment(): void {
    if (this.commentForm.valid) {
      const commentData = { comment: this.commentForm.get('comment')?.value };
      const sub = this.httpService.saveEnquiryComment(this.enquiryId, commentData).subscribe({
        next: () => {
          this.closeCommentDrawer();
          this.getComments();
        }
      });
      this.subscriptions.add(sub);
    }
  }
}
